import { useMutation, useQuery } from '@apollo/client';
import { SelectBox, TextBox } from '@homex/se-react-components';
import { loader } from 'graphql.macro';
import React from 'react';
import { useSelector } from 'react-redux';

import { setLeadSourceCompanionFieldValue, setLeadSourceId } from 'actions';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { Phrase } from 'components/Phrase';
import { ReactComponent as InputMegaphoneSvg } from 'components/svg/InputMegaphone.svg';
import { ReactComponent as InputPersonSvg } from 'components/svg/InputPerson.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { CarouselBottomButtonType, IState, ServiceType } from 'typings';
import {
  ILeadSourcesQuery,
  ILeadSourcesQueryVariables,
  ISetLeadSourceMutation,
  ISetLeadSourceMutationVariables,
} from 'typings/GraphQL';

import {
  ContainerBottom,
  SelectBoxContainer,
  SuccessMessage,
  SuccessMessageBody,
  SuccessMessageHeader,
  TextBoxContainer,
} from './styles';

const leadSourcesQuery = loader('../../queries/leadSources.graphql');

const setLeadSourceMutation = loader('../../queries/setLeadSource.graphql');

export const CarouselItemConfirmFinishSalesBottom = () => {
  const dispatch = useThunkDispatch();
  const {
    appointmentId,
    leadSourceCompanionFieldValue = '',
    leadSourceId,
    serviceType,
  } = useSelector((state: IState) => ({
    appointmentId: state.userInput.appointmentId,
    leadSourceCompanionFieldValue:
      state.userInput.leadSourceCompanionFieldValue,
    leadSourceId: state.userInput.leadSourceId,
    serviceType: state.userInput.serviceType,
  }));

  const [setLeadSource, { called, error, loading: isLoading }] = useMutation<
    ISetLeadSourceMutation,
    ISetLeadSourceMutationVariables
  >(setLeadSourceMutation);

  const { data: leadSourcesData } = useQuery<
    ILeadSourcesQuery,
    ILeadSourcesQueryVariables
  >(leadSourcesQuery, {
    skip: !serviceType,
    variables: {
      serviceType: serviceType || ServiceType.SALES,
    },
  });

  const leadSources = leadSourcesData?.leadSources || [];
  const options = leadSources.map((leadSource) => ({
    companionLabel: leadSource.companionFieldLabel,
    key: leadSource.id,
    label: leadSource.name,
    value: leadSource.id,
  }));

  const selectedLeadSource = leadSources.find(
    (leadSource) => leadSource.id === leadSourceId,
  );
  let { companionFieldLabel = '' } = selectedLeadSource || {};
  companionFieldLabel = companionFieldLabel || '';

  const isCompanionFieldVisible = !!(
    selectedLeadSource && selectedLeadSource.companionFieldLabel
  );

  const isCompanionFieldValid = !isCompanionFieldVisible
    ? true
    : !!leadSourceCompanionFieldValue;
  const isSuccessful = !isLoading && !error && called;
  const errorMessage =
    !isLoading && error && called
      ? Phrase({ id: 'GENERIC_GRAPHQL_ERROR' })
      : '';

  const continueButtonType =
    isSuccessful || !selectedLeadSource
      ? CarouselBottomButtonType.CLOSE
      : CarouselBottomButtonType.CONTINUE;

  const handleSelectBoxChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setLeadSourceId(event.currentTarget.value));
  };

  const handleTextboxChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setLeadSourceCompanionFieldValue(event.currentTarget.value));
  };

  const handleButtonClick = async () => {
    setLeadSource({
      variables: {
        appointmentId: appointmentId || '',
        companionFieldValue: isCompanionFieldVisible
          ? leadSourceCompanionFieldValue
          : null,
        selectedFieldId: leadSourceId || '',
      },
    });
  };

  return (
    <ContainerBottom>
      <SelectBoxContainer isSuccessful={isSuccessful}>
        <SelectBox
          defaultLabel=""
          label={Phrase({ id: 'CONFIRM_FINISH_SALES.LEAD_SOURCE.LABEL' })}
          leftIcon={InputMegaphoneSvg}
          onChange={handleSelectBoxChange}
          options={options}
          value={leadSourceId}
        />
      </SelectBoxContainer>
      <TextBoxContainer
        isSuccessful={isSuccessful}
        isVisible={isCompanionFieldVisible}
      >
        <TextBox
          label={companionFieldLabel}
          leftIcon={InputPersonSvg}
          onChange={handleTextboxChange}
          value={leadSourceCompanionFieldValue}
        />
      </TextBoxContainer>
      {isSuccessful && (
        <SuccessMessage>
          <SuccessMessageHeader>
            <Phrase id="CONFIRM_FINISH_SALES.SUCCESS.HEADER" />
          </SuccessMessageHeader>
          <SuccessMessageBody>
            <Phrase id="CONFIRM_FINISH_SALES.SUCCESS.BODY" />
          </SuccessMessageBody>
        </SuccessMessage>
      )}
      <CarouselContinueButton
        buttonType={continueButtonType}
        errorMessage={errorMessage}
        isDisabled={!isCompanionFieldValid}
        isLoading={isLoading}
        label={Phrase({ id: 'CAROUSEL_CONTINUE_BUTTON.SUBMIT' })}
        onClick={handleButtonClick}
      />
    </ContainerBottom>
  );
};
